// Helper function to get node category (checks both top-level and data.category)
const getNodeCategory = (node) => {
  return node.category || node.data?.category;
};

const validate_publishing = (workflowData) => {
  const errors = {};

  if (!workflowData || !workflowData.nodes || workflowData.nodes.length <= 1) {
    errors.message = "Please add at least a submission and a review nodes before saving.";
    return errors;
  }

  if (!workflowData || !workflowData.edges || workflowData.edges.length === 0) {
    errors.message = "Please connect the nodes before saving.";
    return errors;
  }

  const endNode = workflowData.nodes.find((node) => getNodeCategory(node) === "End");
  if (!endNode) {
    errors.message = "Please add an end node to the workflow.";
    return errors;
  }

  // Ensure there is at least one output in the workflow
  const outputNodes = workflowData.nodes.filter((node) => getNodeCategory(node) === "End");
  if (outputNodes.length === 0) {
    errors.message = "The workflow must have at least one output node.";
    return errors;
  }

  const formStageNodes = workflowData.nodes.filter((node) => node.type === "FormStage");
  if (formStageNodes.length === 0) {
    errors.message = "Please add at least one form stage node to the workflow.";
    return errors;
  }

  const formStageIds = formStageNodes.map((node) => node.id);
  const formStageEdges = workflowData.edges.filter((edge) => formStageIds.includes(edge.source));
  if (formStageEdges.length === 0) {
    errors.message = "Please connect the form stage nodes to the workflow.";
    return errors;
  }

  // Validate that all nodes (except "output" types) have at least one source and one target point connected
  const invalidNodes = workflowData.nodes.filter((node) => {
    const category = getNodeCategory(node);
    
    if (category === "End") {
      // "Output" nodes must have at least one incoming edge
      const hasIncomingEdge = workflowData.edges.some((edge) => edge.target === node.id);
      return !hasIncomingEdge;
    }

    if (category === "Start") {
      // "Input" nodes must have at least one outgoing edge and no incoming edges
      const hasOutgoingEdge = workflowData.edges.some((edge) => edge.source === node.id);
      const hasIncomingEdge = workflowData.edges.some((edge) => edge.target === node.id);
      return !hasOutgoingEdge || hasIncomingEdge;
    }

    // All other nodes must have both incoming and outgoing edges
    const hasIncomingEdge = workflowData.edges.some((edge) => edge.target === node.id);
    const hasOutgoingEdge = workflowData.edges.some((edge) => edge.source === node.id);
    return !(hasIncomingEdge && hasOutgoingEdge);
  });

  if (invalidNodes.length > 0) {
    errors.message = "Please make sure all nodes are properly connected.";
    return errors;
  }

  // Additional Validations
  const submissionNodes = workflowData.nodes.filter(
    (node) => getNodeCategory(node) === "Submission"
  );

  const reviewNodes = workflowData.nodes.filter(
    (node) => getNodeCategory(node) === "Review"
  );

  // Validate that both Submission and Review stages exist
  if (submissionNodes.length === 0 || reviewNodes.length === 0) {
    errors.message = "The workflow must have both Submission and Review stages.";
    return errors;
  }

  // Validate that each Submission node is connected to at least one Review node
  for (const submissionNode of submissionNodes) {
    const submissionFormId = submissionNode.data.formConfig.form_id;
    const submissionName = submissionNode.data.formConfig.name;

    // Find all outgoing edges from this submission node to review nodes
    const outgoingEdges = workflowData.edges.filter((edge) => edge.source === submissionNode.id);
    const connectedReviewNodes = outgoingEdges
      .map((edge) => reviewNodes.find((node) => node.id === edge.target))
      .filter(Boolean);

    if (connectedReviewNodes.length === 0) {
      errors.message = `Submission node ${submissionName} must have at least one edge leading to a Review node.`;
      return errors;
    }

    // Check form_id of both submission and connected review nodes
    for (const reviewNode of connectedReviewNodes) {
      const reviewFormId = reviewNode.data.formConfig.form_id;
      const reviewName = reviewNode.data.formConfig.name;
      if (submissionFormId !== reviewFormId) {
        errors.message = `Submission node ${submissionName} is connected to Review node ${reviewName} with a different form. Both must use the same form.`;
        return errors;
      }
    }
  }

  // Validate that each Submission node has multiple edges leading to Review nodes if applicable
  for (const submissionNode of submissionNodes) {
    const outgoingEdges = workflowData.edges.filter((edge) => edge.source === submissionNode.id);
    const connectedReviewNodes = outgoingEdges.filter((edge) =>
      reviewNodes.some((node) => node.id === edge.target)
    );

    if (connectedReviewNodes.length === 0) {
      errors.message = `Submission node ${submissionNode.id} must have at least one edge leading to a Review node.`;
      return errors;
    }
  }

  // Check for edges where source and target are the same node
  const selfLoopEdges = workflowData.edges.filter((edge) => edge.source === edge.target);
  if (selfLoopEdges.length > 0) {
    const selfLoopNodeIds = selfLoopEdges.map((edge) => edge.source);
    const selfLoopErrorMessage = `The following nodes have edges pointing to themselves: ${selfLoopNodeIds.join(", ")}.`;
    errors.message = selfLoopErrorMessage;
    return errors;
  }

  // --- Child-app Approval Node Validation ---
  const childAppApprovalNodes = workflowData.nodes.filter(
    node => getNodeCategory(node) === "child-app"
  );

  // This validation is for child-app approval nodes that are connected to End or Submission nodes
  for (const approvalNode of childAppApprovalNodes) {
    // Can only connect to End or Submission node
    const outgoingEdges = workflowData.edges.filter((edge) => edge.source === approvalNode.id);
    for (const edge of outgoingEdges) {
      const targetNode = workflowData.nodes.find(node => node.id === edge.target);
      const targetCategory = getNodeCategory(targetNode);
      if (!targetNode || (targetCategory !== "End" && targetCategory !== "Submission")) {
        errors.message = `Approval node "${approvalNode?.data?.name}" can only be connected to a Submission node or an End node.`;
        return errors;
      }
    }
  }

  // Send Back Node Validation. It should be connected to a Submission node
  const sendBackNodes = workflowData.nodes.filter(
    node => getNodeCategory(node) === "child-sbk"
  );

  for (const sendBackNode of sendBackNodes) {
    const outgoingEdges = workflowData.edges.filter(edge => edge.source === sendBackNode.id);
    const isConnectedToSubmission = outgoingEdges.some(edge => {
      const targetNode = workflowData.nodes.find(node => node.id === edge.target);
      return targetNode && getNodeCategory(targetNode) === "Submission";
    });

    if (!isConnectedToSubmission) {
      errors.message = `Send back node "${sendBackNode?.data?.name}" must be connected to a Submission node.`;
      return errors;
    }
  }

  // Reject Node Validation. It should be connected to an End node
  const rejectNodes = workflowData.nodes.filter(
    node => getNodeCategory(node) === "child-rej"
  );

  for (const rejectNode of rejectNodes) {
    const outgoingEdges = workflowData.edges.filter(edge => edge.source === rejectNode.id);
    const isConnectedToEnd = outgoingEdges.some(edge => {
      const targetNode = workflowData.nodes.find(node => node.id === edge.target);
      return targetNode && getNodeCategory(targetNode) === "End";
    });

    if (!isConnectedToEnd) {
      errors.message = `Reject node "${rejectNode?.data?.name}" must be connected to an End node.`;
      return errors;
    }
  }

  // --- End Node Incoming Validation ---
  const endNodes = workflowData.nodes.filter(node => getNodeCategory(node) === "End");
  for (const endNode of endNodes) {
    // Find all incoming edges to this End node
    const incomingEdges = workflowData.edges.filter(edge => edge.target === endNode.id);
    // Find the source nodes for these edges
    const incomingNodes = incomingEdges
      .map(edge => workflowData.nodes.find(node => node.id === edge.source))
      .filter(Boolean);

    // Filter for child-app and child-rej nodes
    const childAppNodes = incomingNodes.filter(node => getNodeCategory(node) === "child-app");
    const childRejNodes = incomingNodes.filter(node => getNodeCategory(node) === "child-rej");

    const total = childAppNodes.length + childRejNodes.length;

    // Must be either one child-app, or one child-rej, or one of each (total 2, but not both same)
    if (
      !(
        (total === 1 && (childAppNodes.length === 1 || childRejNodes.length === 1)) ||
        (total === 2 && childAppNodes.length === 1 && childRejNodes.length === 1)
      )
    ) {
      errors.message = `End node "${endNode?.data?.name}" must have either one incoming edge from a approval or reject node, or both.`;
      return errors;
    }
  }

  return errors;
};

export default validate_publishing;